import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getSystemSettingByKey } from '../actions/systemSettingActions';
import {
  DEFAULT_SESSION_NAME_CONFIG,
  UPLOAD_SESSION_NAME_SETTING_KEY,
  buildSessionNameFromConfig,
  normalizeSessionNameConfig,
  parseSessionNameConfig,
  setSessionNameConfigCache,
} from '../components/crud/utils/sessionNameConfigUtils';

export const useUploadSessionNameConfig = () => {
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.userLogin?.userInfo);
  const [config, setConfig] = useState(DEFAULT_SESSION_NAME_CONFIG);
  const [settingId, setSettingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const setting = await dispatch(getSystemSettingByKey(UPLOAD_SESSION_NAME_SETTING_KEY));
      const parsed = parseSessionNameConfig(setting?.settingValue);
      const normalized = normalizeSessionNameConfig(parsed);
      setConfig(normalized);
      setSettingId(setting?.id ?? null);
      setSessionNameConfigCache(normalized);
      return { setting, config: normalized };
    } catch (err) {
      const normalized = normalizeSessionNameConfig(DEFAULT_SESSION_NAME_CONFIG);
      setConfig(normalized);
      setSettingId(null);
      setSessionNameConfigCache(normalized);
      setError(err);
      return { setting: null, config: normalized };
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const preview = useMemo(
    () => buildSessionNameFromConfig(config, userInfo),
    [config, userInfo]
  );

  return { config, setConfig, settingId, setSettingId, loading, error, preview, refresh };
};

export default useUploadSessionNameConfig;
